import { call, put, takeEvery } from "redux-saga/effects";

import * as apis from "../../network/apis/adminDashboard";
import { getActivityLogs } from "../../network/apis/activityLogs";
import { showErrorMsg } from "../../utils/Helpers";
import * as actions from "./actions";
import * as types from "./types";

function* getAdminDashboardOverviewSaga() {
  try {
    const response = yield call(apis.getAdminDashboardOverview);
    yield put(actions.getAdminDashboardOverviewResponse(response.data?.data));
  } catch (error) {
    yield put(actions.getAdminDashboardOverviewFailure());
    // A network failure has no `error.response` — the axios interceptor
    // already shows "No internet connection" for it (see errorHandler);
    // showing it again here with an empty message left a blank toast.
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getAdminDashboardQuickActionsSaga() {
  try {
    const response = yield call(apis.getAdminDashboardQuickActions);
    yield put(
      actions.getAdminDashboardQuickActionsResponse(response.data?.data),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardQuickActionsFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getAdminDashboardTopCompaniesSaga() {
  try {
    const response = yield call(apis.getAdminDashboardCompaniesOverview, {
      order_by_number_of_saudi_applicants: "desc",
      order_by_number_of_published_events: "desc",
      items: 3,
    });
    const data = response.data?.data;
    yield put(
      actions.getAdminDashboardTopCompaniesResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardTopCompaniesFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getAdminDashboardCompanySatisfactionSaga() {
  try {
    const response = yield call(apis.getAdminDashboardCompaniesOverview, {
      has_evaluated_events: true,
      order_by_overall_clients_satisfaction: "asc",
      page: -1,
    });
    const data = response.data?.data;
    yield put(
      actions.getAdminDashboardCompanySatisfactionResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardCompanySatisfactionFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getAdminDashboardApplicantEngagementSaga() {
  try {
    // Companies with no value come last in either direction (they are not
    // drawn). Capped at 10: a chart of every company on the platform isn't
    // readable, and "View all companies" covers the rest.
    const response = yield call(apis.getAdminDashboardCompaniesOverview, {
      order_by_junior_applicants_percentage: "desc",
      items: 10,
    });
    const data = response.data?.data;
    yield put(
      actions.getAdminDashboardApplicantEngagementResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardApplicantEngagementFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

const TOP_EVENTS_WINDOW_DAYS = 30;

function* getAdminDashboardTopEventsSaga() {
  try {
    // The 30-day window is the client's to send (rolling, computed per call).
    // Filtered by close date (PROLINE-3877), not launch date: an event that
    // launched over 30 days ago but is still running (or just closed) belongs
    // in the table, and launch-date filtering was dropping it.
    const windowStart = new Date(
      Date.now() - TOP_EVENTS_WINDOW_DAYS * 24 * 60 * 60 * 1000,
    ).toLocaleDateString("en-CA");
    const response = yield call(apis.getAdminDashboardEventsOverview, {
      close_at_start_date: windowStart,
      order_by_number_of_approved_applicants: "desc",
      order_by_created_at: "desc",
      items: 3,
    });
    const data = response.data?.data;
    yield put(
      actions.getAdminDashboardTopEventsResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardTopEventsFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

function* getAdminDashboardRecentActivitySaga() {
  try {
    // First page only (PROLINE-3943): the widget shows the 10 newest entries.
    const response = yield call(getActivityLogs, {
      actor_type: "Admin",
      page: 1,
      items: 10,
    });
    const data = response.data?.data;
    yield put(
      actions.getAdminDashboardRecentActivityResponse(
        Array.isArray(data) ? data : [],
      ),
    );
  } catch (error) {
    yield put(actions.getAdminDashboardRecentActivityFailure());
    const message = error.response?.data?.message;
    if (message) {
      yield call(showErrorMsg, message);
    }
  }
}

export default function* adminDashboardSagas() {
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_OVERVIEW_REQUEST,
    getAdminDashboardOverviewSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_QUICK_ACTIONS_REQUEST,
    getAdminDashboardQuickActionsSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_TOP_COMPANIES_REQUEST,
    getAdminDashboardTopCompaniesSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_TOP_EVENTS_REQUEST,
    getAdminDashboardTopEventsSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_COMPANY_SATISFACTION_REQUEST,
    getAdminDashboardCompanySatisfactionSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_APPLICANT_ENGAGEMENT_REQUEST,
    getAdminDashboardApplicantEngagementSaga,
  );
  yield takeEvery(
    types.GET_ADMIN_DASHBOARD_RECENT_ACTIVITY_REQUEST,
    getAdminDashboardRecentActivitySaga,
  );
}
